import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TONE = { valid: "bg-emerald-100 text-emerald-900", renewal_soon: "bg-amber-100 text-amber-900", expired: "bg-red-200 text-red-900",
  rejected: "bg-red-200 text-red-900", awaiting_renewal: "bg-sky-100 text-sky-900", pending: "bg-violet-100 text-violet-900", missing: "bg-muted" };
const LABEL = { id_recto: "Recto", id_verso: "Verso", driving_license_recto: "Recto", driving_license_verso: "Verso", carte_grise: "Fichier", insurance: "Fichier" };

export function useDriverDocs() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/price/docs/me").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return { data, load };
}

// Mes documents conducteur : statuts, échéances, renouvellement, déclaration sur l'honneur.
export default function DriverDocsCard() {
  const { data, load } = useDriverDocs();
  if (!data) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="space-y-2" data-testid="driver-docs-card">
      {data.block?.active && (
        <div className="rounded-2xl bg-red-100 p-3 text-[13px] font-bold text-red-900 street-border-thin" data-testid="driver-docs-block">{data.block.reason}. Ton compte passager reste actif.</div>
      )}
      {data.honor_required && <HonorDeclaration data={data} onDone={load} />}
      {data.groups.map((g) => <GroupRow key={g.group} g={g} onDone={load} />)}
    </div>
  );
}

function GroupRow({ g, onDone }) {
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const canRenew = ["valid", "renewal_soon", "expired", "rejected", "awaiting_renewal"].includes(g.status);
  const send = async () => {
    setBusy(true);
    try {
      for (const k of g.kinds) {
        if (!files[k]) continue;
        const fd = new FormData(); fd.append("kind", k); fd.append("file", files[k]);
        await api.post("/price/docs/renew/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      }
      await api.post("/price/docs/renew/finalize", { group: g.group });
      toast.success("Document envoyé — vérification par l'équipe Price");
      setOpen(false); setFiles({}); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const missing = g.kinds.filter((k) => !files[k] && !g.renewal_sent.includes(k));
  return (
    <div className="rounded-2xl bg-[#FFF8F1] p-3 street-border-thin" data-testid={`driver-doc-${g.group}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-[Outfit] text-[14px] font-black">{g.label}</div>
          <div className="text-[11px] font-semibold text-foreground/60">
            {g.due ? `${g.next_check_at && g.due === g.next_check_at ? "Renouvellement" : "Expiration"} : ${new Date(`${g.due}T12:00:00`).toLocaleDateString("fr-FR")}` : "Aucune échéance enregistrée"}
            {g.renewal_reason ? ` · ${g.renewal_reason}` : ""}{g.rejection_reason ? ` · Refus : ${g.rejection_reason}` : ""}
          </div>
        </div>
        <span className={`flex-none rounded-full px-2 py-1 text-[10px] font-black ${TONE[g.status] || "bg-muted"}`}>{g.renewal_sent.length ? "Nouveau envoyé" : g.status_label}</span>
      </div>
      {canRenew && !open && (
        <button type="button" onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1 text-[12px] font-black text-primary" data-testid={`driver-doc-renew-${g.group}`}>
          <FileUp className="h-3.5 w-3.5" />Envoyer une nouvelle version
        </button>
      )}
      {open && (
        <div className="mt-2 space-y-2">
          {g.kinds.map((k) => (
            <label key={k} className="flex items-center justify-between gap-2 rounded-xl bg-white p-2 text-[12px] font-bold street-border-thin">
              {LABEL[k]} {files[k] ? `· ${files[k].name}` : ""}
              <input type="file" accept="image/*,application/pdf" onChange={(e) => setFiles({ ...files, [k]: e.target.files?.[0] })} data-testid={`driver-doc-file-${k}`} className="max-w-[55%] text-[11px]" />
            </label>
          ))}
          <button type="button" disabled={busy || missing.length > 0} onClick={send} data-testid={`driver-doc-send-${g.group}`}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-full bg-primary text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}Envoyer pour validation
          </button>
        </div>
      )}
    </div>
  );
}

export function HonorDeclaration({ data, onDone }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try { await api.post("/price/docs/honor", { accepted: true }); toast.success("Déclaration enregistrée"); onDone?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-accent p-3 street-border-thin" data-testid="driver-honor">
      <div className="flex items-center gap-2 font-[Outfit] text-[14px] font-black"><ShieldCheck className="h-4 w-4" />Déclaration sur l&apos;honneur</div>
      <p className="mt-1 text-[12px] font-medium">{data.honor_text}</p>
      <label className="mt-2 flex items-start gap-2 text-[12px] font-black">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 h-4 w-4" data-testid="driver-honor-check" />
        Je certifie sur l&apos;honneur l&apos;exactitude de ces informations.
      </label>
      <button type="button" disabled={!checked || busy} onClick={accept} data-testid="driver-honor-accept"
        className="mt-2 h-10 w-full rounded-full bg-primary text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-50">Valider ma déclaration</button>
    </div>
  );
}
